import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
  TrendingUp, Receipt, Wallet, Store, ShoppingBag, Percent, Loader2, AlertTriangle, ServerCrash, Heart, Wifi, WifiOff, Laptop, PowerOff,
} from "lucide-react";

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EC4899", "#8B5CF6"];

function sinceText(iso) {
  if (!iso) return "never";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function durText(secs) {
  if (!secs || secs < 60) return "under a minute";
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60), rem = m % 60;
  if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function Kpi({ label, value, icon: Icon, sub, testid, onClick }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} className="text-left w-full rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5 hover:border-slate-300 hover:shadow-sm transition-all cursor-pointer">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center"><Icon className="h-4 w-4 text-blue-600" /></div>
      </div>
      <p className="mt-3 text-2xl font-bold text-slate-900 dark:text-white font-admin-head">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      <span className="text-[11px] text-blue-600 mt-2 inline-block">View details →</span>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [low, setLow] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [closingId, setClosingId] = useState(null);

  const loadConn = useCallback(() => {
    api.get("/kiosks/connectivity").then((r) => setKiosks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/reports/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/stock/low").then((r) => setLow(r.data)).catch(() => {});
    if (user?.role !== "finance") {
      api.get("/incidents?status=open").then((r) => setIncidents(r.data)).catch(() => {});
      loadConn();
      const t = setInterval(loadConn, 60000);
      return () => clearInterval(t);
    }
  }, [user, loadConn]);

  const closeDesktop = async (kid) => {
    setClosingId(kid);
    try {
      await api.post(`/kiosks/${kid}/close-session`);
      toast.success("Close signal sent — the desktop session locks and drops offline within ~30s.");
      setTimeout(loadConn, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close the session");
    } finally {
      setClosingId(null);
    }
  };

  if (!data) return <div className="h-full flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const m = data.metrics;
  const openTile = (key) => navigate(`/admin/insights/${key}`);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
        <p className="text-slate-500 mt-1">Welcome back, {user?.name}.</p>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Kpi testid="kpi-gross-sales" label="Gross Sales" value={gbp(m.gross_sales)} icon={TrendingUp} sub={`${m.transactions} transactions`} onClick={() => openTile("gross")} />
        <Kpi testid="kpi-vat" label="VAT Collected" value={gbp(m.vat)} icon={Percent} sub="Output VAT" onClick={() => openTile("vat")} />
        <Kpi testid="kpi-net-sales" label="Net Sales (excl VAT)" value={gbp(m.net_sales)} icon={Wallet} sub={`Profit ${gbp(m.gross_profit)}`} onClick={() => openTile("net")} />
        <Kpi testid="kpi-avg-basket" label="Avg Transaction" value={gbp(m.avg_transaction_value)} icon={ShoppingBag} sub={`${m.units_sold} units sold`} onClick={() => openTile("avg")} />
        <Kpi testid="kpi-fees" label="Stripe Fees" value={gbp(m.processing_fees)} icon={Receipt} sub={`Net ${gbp(m.revenue_after_fees)}`} onClick={() => openTile("fees")} />
        <Kpi testid="kpi-contribution" label="Contribution" value={gbp(m.contribution_after_fees)} icon={TrendingUp} sub="After COGS & fees" onClick={() => openTile("contribution")} />
        <Kpi testid="kpi-kiosks" label="Active Kiosks" value={`${m.active_kiosks}/${m.total_kiosks}`} icon={Store} sub="Live kiosks" onClick={() => openTile("kiosks")} />
        <Kpi testid="kpi-failed" label="Failed / Cancelled" value={m.failed_transactions} icon={AlertTriangle} sub={`${m.refunds} refunds`} onClick={() => openTile("failed")} />
      </div>

      {m.charity_enabled_anywhere && (
        <div data-testid="dashboard-charity-tile" className="rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50/60 dark:bg-emerald-950/30 p-5 flex items-center gap-4">
          <div className="h-11 w-11 rounded-lg bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center"><Heart className="h-5 w-5 text-emerald-600" /></div>
          <div>
            <p className="text-sm text-emerald-700 dark:text-emerald-300">Raised for charity this month{m.charity_name_month ? ` · ${m.charity_name_month}` : ""}</p>
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-200 font-admin-head">{gbp(m.charity_month || 0)}</p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Sales trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" fontSize={11} stroke="#94a3b8" />
              <YAxis fontSize={11} stroke="#94a3b8" />
              <Tooltip formatter={(v) => gbp(v)} />
              <Line type="monotone" dataKey="gross" stroke="#3B82F6" strokeWidth={2.5} dot={false} name="Gross" />
              <Line type="monotone" dataKey="net" stroke="#10B981" strokeWidth={2} dot={false} name="Net" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Sales by category</h3>
          {data.category_split.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.category_split} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => e.name}>
                  {data.category_split.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => gbp(v)} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center mt-16">No sales yet</p>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Top products</h3>
          {data.top_products.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={140} fontSize={11} stroke="#94a3b8" />
                <Tooltip formatter={(v) => gbp(v)} />
                <Bar dataKey="revenue" fill="#3B82F6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center mt-16">No sales yet</p>}
        </div>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Low stock alerts</h3>
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {low.length ? low.map((l) => (
              <div key={l.id} data-testid={`low-stock-${l.product_id}`} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{l.product_name}</p>
                  <p className="text-xs text-slate-400">{l.kiosk_name}</p>
                </div>
                <span className="font-bold text-amber-600">{l.stock_qty} left</span>
              </div>
            )) : <p className="text-sm text-slate-400">All stock levels healthy.</p>}
          </div>
        </div>
      </div>

      {user?.role !== "finance" && (() => {
        const desktopHeld = kiosks.filter((k) => k.activated && k.status === "online" && k.last_client === "Desktop browser");
        if (!desktopHeld.length) return null;
        return (
          <div data-testid="desktop-guard" className="rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 p-5">
            <div className="flex items-center gap-2 mb-2">
              <Laptop className="h-4 w-4 text-amber-600" />
              <h3 className="font-semibold text-amber-800 dark:text-amber-200">Kept online by a computer browser</h3>
              <span data-testid="desktop-guard-count" className="ml-1 text-xs font-bold bg-amber-200 dark:bg-amber-900 text-amber-800 dark:text-amber-200 px-2 py-0.5 rounded-full">{desktopHeld.length}</span>
            </div>
            <p className="text-xs text-amber-700/80 dark:text-amber-300/70 mb-3">These kiosks are reporting “online” from a PC/Mac browser, not the kiosk tablet. Close the stray session so the true status shows — your iPad kiosks are never affected.</p>
            <div className="space-y-2">
              {desktopHeld.map((k) => (
                <div key={k.id} data-testid={`desktop-guard-kiosk-${k.id}`} className="flex items-center justify-between gap-3 text-sm py-2 border-b border-amber-200/60 dark:border-amber-900/40 last:border-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Laptop className="h-4 w-4 text-amber-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 dark:text-white truncate">{k.name} <span className="text-xs text-slate-400 font-normal">{k.code}</span></p>
                      <p className="text-xs text-amber-700/70 dark:text-amber-300/60">Desktop browser · last seen {sinceText(k.last_seen)}</p>
                    </div>
                  </div>
                  <button
                    data-testid={`desktop-guard-close-${k.id}`}
                    onClick={() => closeDesktop(k.id)}
                    disabled={closingId === k.id}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-3 py-1.5 text-xs font-semibold"
                  >
                    {closingId === k.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><PowerOff className="h-3.5 w-3.5" /> Close session</>}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {user?.role !== "finance" && (() => {
        const live = kiosks.filter((k) => k.activated && k.status !== "maintenance");
        const onlineCt = live.filter((k) => k.status === "online").length;
        const offline = live.filter((k) => k.status === "offline");
        const draftCt = kiosks.filter((k) => !k.activated).length;
        const maintCt = kiosks.filter((k) => k.status === "maintenance").length;
        return (
          <div data-testid="kiosk-connectivity" className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Wifi className="h-4 w-4 text-emerald-500" /> Kiosk connectivity</h3>
              <div className="flex items-center gap-4 text-xs">
                <span data-testid="kiosk-online-count" className="flex items-center gap-1.5 text-emerald-600 font-semibold"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {onlineCt} online</span>
                <span data-testid="kiosk-offline-count" className={`flex items-center gap-1.5 font-semibold ${offline.length ? "text-rose-600" : "text-slate-400"}`}><span className={`h-2 w-2 rounded-full ${offline.length ? "bg-rose-500" : "bg-slate-300"}`} /> {offline.length} offline</span>
                {maintCt > 0 && <span className="flex items-center gap-1.5 text-amber-600 font-semibold"><span className="h-2 w-2 rounded-full bg-amber-500" /> {maintCt} maintenance</span>}
                <span className="flex items-center gap-1.5 text-slate-400">{draftCt} draft</span>
              </div>
            </div>
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {offline.length ? offline.map((k) => (
                <button key={k.id} data-testid={`offline-kiosk-${k.id}`} onClick={() => navigate("/admin/hierarchy")} className="w-full flex items-center justify-between text-sm py-2 border-b border-slate-100 dark:border-slate-800 text-left hover:bg-slate-50 dark:hover:bg-slate-900 rounded px-1 transition-colors">
                  <div className="flex items-center gap-2.5">
                    <WifiOff className="h-4 w-4 text-rose-500 shrink-0" />
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{k.name} <span className="text-xs text-slate-400 font-normal">{k.code}</span></p>
                      <p className="text-xs text-slate-400">Down for {durText(k.down_seconds)} · {durText(k.downtime_month_seconds)} offline this month</p>
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-rose-600 shrink-0 text-right">Last seen {sinceText(k.last_seen)}</span>
                </button>
              )) : <div className="flex items-center gap-2 text-sm text-slate-400 py-3"><Wifi className="h-4 w-4 text-emerald-500" /> All live kiosks are online.</div>}
            </div>
          </div>
        );
      })()}

      {user?.role !== "finance" && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4 flex items-center gap-2"><ServerCrash className="h-4 w-4 text-red-500" /> Open kiosk incidents</h3>
          <div className="space-y-2 max-h-[240px] overflow-y-auto">
            {incidents.length ? incidents.map((i) => (
              <div key={i.id} data-testid={`incident-${i.id}`} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 dark:border-slate-800">
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{i.kiosk_name} <span className="font-mono-x text-xs text-slate-400">{i.code}</span></p>
                  <p className="text-xs text-slate-400">{i.location_name} · opened {(i.opened_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${i.escalated ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{i.escalated ? "Escalated" : "Offline"}</span>
              </div>
            )) : <p className="text-sm text-slate-400">No open incidents. All kiosks healthy.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
